// Shared client types + fetch helpers for the clicker app catalog and the per-team
// availability list. Used by the catalog editor tab (clicker-apps-tab.ts), the team
// page's availability list (team-form-view.ts) and the per-device launch panel
// dropdown (clicker-launch-panel.ts). Split out so those don't import each other
// just to agree on the shapes.
import { t } from '../ui/lang';
import { farmLocationFlag } from '../core/farm-locations';
/** What a device with no stored config is set to — mirrors the server's DEFAULT_TASK_TYPE. */
export const DEFAULT_TASK_TYPE = 'payout';
/** How an app is named in a dropdown/list: its country flag (when set) + the name. */
export function clickerAppLabel(app) {
    const flag = farmLocationFlag(app.country);
    return flag ? `${flag} ${app.name}` : app.name;
}
/**
 * Whether an app can run a task type — mirrors appSupportsTask() on the server
 * (server/clickerApps.ts), which is the authority. `auto` runs both flows on one
 * device, so it needs BOTH capabilities; it is derived, not a third catalog flag.
 */
export function appSupportsTask(app, taskType) {
    if (taskType === 'deposit')
        return app.supports_deposit;
    if (taskType === 'payout')
        return app.supports_payout;
    return app.supports_deposit && app.supports_payout;
}
/** Whole clicker app catalog (sorted server-side). */
export async function fetchClickerApps() {
    const res = await fetch('/api/clicker-apps');
    if (!res.ok)
        throw new Error(t('clickertab.failed_to_load_clicker_apps'));
    return await res.json();
}
/** A team's available app names (empty = everything allowed). Superadmin-only. */
export async function fetchTeamClickerApps(teamId) {
    const res = await fetch(`/api/teams/${teamId}/clicker-apps`);
    if (!res.ok)
        throw new Error(t('clickertab.failed_to_load_team_availability'));
    return await res.json();
}
/** Replace a team's whole availability list with these app names. Superadmin-only. */
export async function saveTeamClickerApps(teamId, appNames) {
    const res = await fetch(`/api/teams/${teamId}/clicker-apps`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(appNames),
    });
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error ?? t('clickertab.failed_to_save_availability'));
    }
}
/** Apps available to launch on a farm's devices (catalog filtered by team availability). */
export async function fetchFarmClickerApps(farmId) {
    const res = await fetch(`/api/farms/${farmId}/clicker/apps`);
    if (!res.ok)
        throw new Error(t('clickertab.failed_to_load_available_apps'));
    return await res.json();
}
